/**
 * Client-side email HTML builder for an accurate preview.
 *
 * This MIRRORS the server-side construction in
 * functions/CRM/email/emailUtils.js -> sendGmailEmail():
 *   1. preserveBlankLines:   <p></p>  ->  <p><br></p>
 *   2. wrap in the same <html><body style="..."> document
 *   3. resetParagraphSpacing: merge margin:0;padding:0 into each <p>
 *      WITHOUT discarding existing inline styles (e.g. text-align)
 *   4. injectSignature:       append the Gmail signature block
 *
 * Keep this in sync with emailUtils.js so the preview stays 100% accurate to
 * what the recipient receives. (Open-tracking pixel / click-tracking link
 * rewrites are intentionally omitted -- they are invisible to the recipient.)
 */

const BODY_STYLE = 'font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5; color: #333;';

/** Mirror of server preserveBlankLines(). */
function preserveBlankLines(html) {
  if (!html) return html || '';
  return html.replace(/<p><\/p>/g, '<p><br></p>');
}

/** Mirror of server resetParagraphSpacing(): merge margin/padding, keep other styles. */
function resetParagraphSpacing(rootDoc) {
  rootDoc.querySelectorAll('p').forEach((p) => {
    const existing = (p.getAttribute('style') || '').trim();
    const hasMargin = /(?:^|;)\s*margin\b/i.test(existing);
    const hasPadding = /(?:^|;)\s*padding\b/i.test(existing);
    let merged = existing && !existing.endsWith(';') ? `${existing};` : existing;
    if (!hasMargin) merged += 'margin:0;';
    if (!hasPadding) merged += 'padding:0;';
    p.setAttribute('style', merged);
  });
}

/**
 * Build the full HTML document exactly as the server will send it.
 * @param {string} rawBody - editor HTML body (variables already resolved)
 * @param {{ signature?: string }} [opts]
 * @returns {string} full <!DOCTYPE html> document string
 */
export function buildEmailHtml(rawBody, opts = {}) {
  const { signature } = opts;
  const body = preserveBlankLines(rawBody || '');

  const docHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head><body style="${BODY_STYLE}">${body}</body></html>`;

  if (typeof DOMParser === 'undefined') {
    // SSR / non-browser fallback: return the unparsed document.
    return docHtml;
  }

  const parsed = new DOMParser().parseFromString(docHtml, 'text/html');
  resetParagraphSpacing(parsed);

  if (signature) {
    const sig = parsed.createElement('div');
    sig.className = 'gmail_signature';
    sig.setAttribute('style', 'margin-top:16px;');
    sig.innerHTML = signature;
    parsed.body.appendChild(sig);
  }

  return `<!DOCTYPE html>${parsed.documentElement.outerHTML}`;
}

/**
 * Sample variable values used when a preview has no real contact/deal context
 * (e.g. the admin Template Manager or a campaign node). Covers both the flat
 * keys ({{firstName}}) and namespaced keys ({{contact.firstName}}) the server
 * supports.
 */
export const SAMPLE_VARS = {
  firstName: 'John',
  lastName: 'Smith',
  email: 'john.smith@example.com',
  phone: '(555) 123-4567',
  company: 'Smith & Associates',
  dealName: 'Smith Home Purchase',
  loanAmount: '$450,000',
  amount: '$450,000',
  product: '30-Year Fixed',
  stage: 'Processing',
  loanNumber: 'LN-2026-001234',
  lender: 'First National Bank',
  loanPurpose: 'Purchase',
  propertyAddress: '123 Main St, Austin, TX 78701',
  estimatedClosingDate: 'Apr 15, 2026',
  senderName: 'Jane Doe',
  senderEmail: 'jane.doe@future.loans',
  senderPhone: '(555) 987-6543',
  loanOfficerName: 'Jane Doe',
  'contact.firstName': 'John',
  'contact.lastName': 'Smith',
  'contact.email': 'john.smith@example.com',
  'contact.phone': '(555) 123-4567',
  'contact.company': 'Smith & Associates',
  'deal.dealName': 'Smith Home Purchase',
  'deal.loanAmount': '$450,000',
  'deal.amount': '$450,000',
  'deal.propertyAddress': '123 Main St, Austin, TX 78701',
  'deal.estimatedClosingDate': 'Apr 15, 2026',
  'sender.name': 'Jane Doe',
  'sender.email': 'jane.doe@future.loans',
  'sender.phone': '(555) 987-6543',
  // Deal team role namespaces (Phase 5 — see functions/CRM/utils/dealTeamContext.js)
  'owner.name': 'Jane Doe',
  'owner.firstName': 'Jane',
  'owner.email': 'jane.doe@future.loans',
  'owner.phone': '(555) 987-6543',
  'primaryOwner.name': 'Jane Doe',
  'primaryOwner.firstName': 'Jane',
  'csc.name': 'Casey Coordinator',
  'csc.firstName': 'Casey',
  'csc.email': 'casey@future.loans',
  'coOwner.name': 'Chris Co-Owner',
  'coOwner.firstName': 'Chris',
  'listingAgent.fullName': 'Lisa Lister',
  'listingAgent.firstName': 'Lisa',
  'listingAgent.email': 'lisa@realty.com',
  'buyersAgent.fullName': 'Amy Agent',
  'buyersAgent.firstName': 'Amy',
  'buyersAgent.email': 'amy@acmerealty.com',
  'titleCompany.fullName': 'Tina Title',
  'titleCompany.firstName': 'Tina',
  'titleCompany.company': 'Sure Title Co',
  'titleCompany.email': 'tina@suretitle.com',
};

/**
 * Resolve {{variable}} placeholders the way the server does: replace known keys
 * with their values, then strip any remaining unresolved {{...}} placeholders.
 * @param {string} html
 * @param {Record<string,string>} [variables]
 */
export function resolveTemplateVariables(html, variables) {
  if (!html) return '';
  if (!variables || typeof variables !== 'object') {
    return html.replace(/\{\{[^}]+\}\}/g, '');
  }
  let result = html;
  Object.entries(variables).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(`\\{\\{${escapedKey}\\}\\}`, 'g'), String(value));
  });
  return result.replace(/\{\{[^}]+\}\}/g, '');
}
